import { httpService } from "./http.service";

// Dashboard backend base URL - direct access without proxy
const DASHBOARD_BASE_URL = "/dashboard";

// Types for dashboard API responses
export interface MetricValue {
  metricName: string;
  value: string | null | DoctorMetric[] | ClaimEfficiency;
}

export interface DoctorMetric {
  doctor: string;
  charges: string;
  payments: string;
}

export interface ClaimEfficiency {
  unbilled_count: string;
  billed_count: string;
}

export interface Widget {
  id: string;
  name: string;
}

export interface Doctor {
  id: string;
  name: string;
}

export interface JCodesResponse {
  codes: string[];
}

export interface ARPeriodResponse {
  [date: string]: { metricName: string; value: string }[];
}

// Period options for filters
export type PeriodType =
  | "current_week"
  | "previous_week"
  | "current_month"
  | "previous_month"
  | "year_to_date"
  | "last_year"
  | "last_90_days"
  | "custom";

export interface DateRange {
  startDate: string; // ISO date string
  endDate: string; // ISO date string
}

// Payor role options
export type PayerRole =
  | "primary"
  | "secondary"
  | "tertiary"
  | "patient"
  | "primary_secondary"
  | "all";

class DashboardService {
  /**
   * Get list of all available widgets
   */
  async getWidgets(): Promise<Widget[]> {
    return httpService.get<Widget[]>(`${DASHBOARD_BASE_URL}/widgets`);
  }

  // Deprecated: backend moved to dedicated endpoints under /widget/metrics/*
  // Keeping temporarily for backward compatibility (no current usages expected)
  async getMetrics(_widgetId: string, _params?: Record<string, string>): Promise<MetricValue[]> {
    throw new Error(
      "getMetrics is deprecated. Use dedicated methods like getFinancialHealthMetrics, getChargesPayments, etc."
    );
  }

  /**
   * Financial Health metrics
   * GET /dashboard/widget/metrics/finance?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
   */
  async getFinancialHealthMetrics(
    startDate: string,
    endDate: string,
    payRole?: string
  ): Promise<MetricValue[]> {
    const params: Record<string, string> = { startDate, endDate };
    if (payRole && payRole !== "ALL") params.payRole = payRole;
    const qs = new URLSearchParams(params).toString();
    return httpService.get<MetricValue[]>(`${DASHBOARD_BASE_URL}/widget/metrics/finance?${qs}`);
  }

  /**
   * Claim Efficiency metrics
   * GET /dashboard/widget/metrics/claim-efficiency?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
   */
  async getClaimEfficiency(startDate: string, endDate: string): Promise<MetricValue[]> {
    const qs = new URLSearchParams({ startDate, endDate }).toString();
    return httpService.get<MetricValue[]>(
      `${DASHBOARD_BASE_URL}/widget/metrics/claim-efficiency?${qs}`
    );
  }

  /**
   * Charges & Payments (practice + by doctors) without J-Codes block
   * GET /dashboard/widget/metrics/charges?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
   */
  async getChargesPayments(startDate: string, endDate: string): Promise<MetricValue[]> {
    const qs = new URLSearchParams({ startDate, endDate }).toString();
    return httpService.get<MetricValue[]>(`${DASHBOARD_BASE_URL}/widget/metrics/charges?${qs}`);
  }

  /**
   * J-Codes Charges block for Charges | Payments widget
   * GET /dashboard/widget/metrics/jcodes-charges?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD[&jcode=...]
   */
  async getJCodesCharges(
    startDate: string,
    endDate: string,
    jcode?: string
  ): Promise<MetricValue[]> {
    const params: Record<string, string> = { startDate, endDate };
    if (jcode && jcode !== "all") params.jcode = jcode;
    const qs = new URLSearchParams(params).toString();
    return httpService.get<MetricValue[]>(
      `${DASHBOARD_BASE_URL}/widget/metrics/jcodes-charges?${qs}`
    );
  }

  /**
   * J-Code AR metrics
   * GET /dashboard/widget/metrics/jcodes-ar?dateType=CHARGE_CREATED_DATE|BILLING_PROVIDER_CREATED_DATE
   */
  async getJCodeAR(dateType: string, jcode?: string): Promise<MetricValue[]> {
    const params: Record<string, string> = { dateType };
    if (jcode && jcode !== "all") params.jcode = jcode;
    const qs = new URLSearchParams(params).toString();
    return httpService.get<MetricValue[]>(`${DASHBOARD_BASE_URL}/widget/metrics/jcodes-ar?${qs}`);
  }

  /**
   * Accounts Receivable metrics (Insurance)
   * GET /dashboard/widget/metrics/accounts-receivable
   */
  async getAccountsReceivable(): Promise<MetricValue[]> {
    return httpService.get<MetricValue[]>(
      `${DASHBOARD_BASE_URL}/widget/metrics/accounts-receivable`
    );
  }

  /**
   * Accounts Receivable metrics by responsibility type (Insurance)
   * GET /dashboard/widget/metrics/accounts-receivable-responsibility-type?responsibilityType=ALL|PRIMARY|SECONDARY|...
   */
  async getAccountsReceivableByResponsibilityType(
    responsibilityType: "ALL" | "PRIMARY" | "SECONDARY" | "PRIMARY_SECONDARY" | "TERTIARY"
  ): Promise<MetricValue[]> {
    const qs = new URLSearchParams({ responsibilityType }).toString();
    return httpService.get<MetricValue[]>(
      `${DASHBOARD_BASE_URL}/widget/metrics/accounts-receivable-responsibility-type?${qs}`
    );
  }

  /**
   * Accounts Receivable metrics (Patient)
   * GET /dashboard/widget/metrics/accounts-receivable-patients
   */
  async getAccountsReceivablePatients(): Promise<MetricValue[]> {
    return httpService.get<MetricValue[]>(
      `${DASHBOARD_BASE_URL}/widget/metrics/accounts-receivable-patients`
    );
  }

  /**
   * Accounts Receivable by period
   * GET /dashboard/widget/metrics/accounts-receivable-period?period=WEEKLY|MONTHLY[&limit=N]
   */
  async getAccountsReceivablePeriod(period: string, limit?: number): Promise<ARPeriodResponse> {
    const params: Record<string, string> = { period };
    if (limit !== undefined) params.limit = String(limit);
    const qs = new URLSearchParams(params).toString();
    return httpService.get<ARPeriodResponse>(
      `${DASHBOARD_BASE_URL}/widget/metrics/accounts-receivable-period?${qs}`
    );
  }

  /**
   * Get list of doctors
   */
  async getDoctors(): Promise<Doctor[]> {
    return httpService.get<Doctor[]>(`${DASHBOARD_BASE_URL}/data/doctors`);
  }

  /**
   * Get list of J-Codes
   */
  async getJCodes(): Promise<string[]> {
    const response = await httpService.get<JCodesResponse>(`${DASHBOARD_BASE_URL}/data/jcodes`);
    return response.codes;
  }

  /**
   * Build query parameters for period selection (UI helper only)
   * Backend expects explicit startDate/endDate when needed.
   */
  buildPeriodParams(
    periodType: PeriodType,
    _daysCount?: number,
    dateRange?: DateRange
  ): Record<string, string> {
    const params: Record<string, string> = {};

    switch (periodType) {
      case "current_month":
      case "previous_month":
        // Caller should compute concrete startDate/endDate; no period param is sent
        break;
      case "custom":
        if (dateRange) {
          params.startDate = dateRange.startDate;
          params.endDate = dateRange.endDate;
        }
        break;
    }

    return params;
  }

  /**
   * Build query parameters for payor role selection (UI state only, not sent to backend now)
   */
  buildPayerRoleParams(_payerRole: PayerRole): Record<string, string> {
    return {};
  }
}

export const dashboardService = new DashboardService();
